'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { FC, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { subscribeMailchimp } from '@/app/actions/subscribe-mailchimp';
import { GuidelineFormDto } from '@/components/BrandingPage/GuidelineForm/types';
import AnimatedButton from '@/components/shared/AnimatedButton';
import Input from '@/components/shared/Fields/input';
import Honeypot, { HoneypotHandle } from '@/components/shared/Honeypot';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import routes from '@/constants/routes';
import { toastErrorStyles } from '@/constants/toast';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useModal } from '@/hooks/useModal';
import { clearUtmData, getUtmData } from '@/hooks/useUtmTracking';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';

import css from './style.module.scss';

const schema = z.object({
	firstName: z.string().min(2, 'First name is required'),
	lastName: z.string().optional(),
	email: z.email('Invalid email')
});

type FormData = z.infer<typeof schema>;

type VideoSources = {
	mov?: string;
	webm?: string;
	mp4?: string;
};

const VIDEO_SOURCE_META: { key: keyof VideoSources; type: string }[] = [
	{ key: 'mov', type: 'video/quicktime; codecs="hvc1"' },
	{ key: 'webm', type: 'video/webm' },
	{ key: 'mp4', type: 'video/mp4' }
];

const DEFAULT_VIDEO_SOURCES: VideoSources = {
	mp4: '/assets/video/mp4/brandbook-with-head.mp4',
	webm: '/assets/video/webm/brandbook-with-head.webm'
};

const CAPACITY_CRISIS_THEME_VIDEO_SOURCES: VideoSources = {
	mp4: '/assets/video/mp4/capacity-crisis-with-head.mp4',
	webm: '/assets/video/webm/capacity-crisis-with-head.webm'
};

const HEADS_BLACK_THEME_VIDEO_SOURCES: VideoSources = {
	mp4: '/assets/video/mp4/feedback.mp4'
};

const VIDEO_SOURCES_BY_VARIATION: Record<
	NonNullable<GuidelineFormDto['variationVideo']>,
	VideoSources
> = {
	brandbook: DEFAULT_VIDEO_SOURCES,
	'capacity-crisis': CAPACITY_CRISIS_THEME_VIDEO_SOURCES,
	'heads-black': HEADS_BLACK_THEME_VIDEO_SOURCES
};

interface Props {
	data: GuidelineFormDto;
}

const GuidelineForm: FC<Props> = ({ data }) => {
	const { openModal } = useModal();
	const {
		control,
		handleSubmit,
		reset,
		formState: { errors, isValid, isSubmitting }
	} = useForm<FormData>({
		resolver: zodResolver(schema),
		mode: 'onChange'
	});

	const honeypotRef = useRef<HoneypotHandle>(null);
	const sectionRef = useRef<HTMLElement>(null);
	const [isSuccess, setIsSuccess] = useState(false);
	const mailchimpTag = data.mailchimpTag || 'brandbook_request';
	const videoSources =
		VIDEO_SOURCES_BY_VARIATION[data.variationVideo as keyof typeof VIDEO_SOURCES_BY_VARIATION] ??
		DEFAULT_VIDEO_SOURCES;

	const shouldLoadVideo = useLazyVideoSource(sectionRef);
	useViewportVideosPlayback([sectionRef]);

	const onSubmit = async (formData: FormData) => {
		const finishSuccess = () => {
			openModal('BRANDING_GUIDELINE_SUCCESS', { data });
			clearUtmData();
			reset();
			setIsSuccess(true);
		};

		if (honeypotRef.current?.isFilled()) {
			finishSuccess();
			return;
		}

		try {
			const utmData = getUtmData();

			await subscribeMailchimp({
				name: [formData.firstName, formData.lastName]
					.filter(Boolean)
					.map(name => name!.trim())
					.join(' '),
				email: formData.email,
				tags: [mailchimpTag],
				utmData
			});

			finishSuccess();
		} catch (err) {
			console.error(err);
			toast.error('Error submitting form', toastErrorStyles);
		}
	};

	return (
		<section ref={sectionRef} className={clsx(css.wrapper, 'container')}>
			<div className={css.content}>
				<Typography uppercase variant="h3" tag="h2" className={css.title}>
					<StrapiRichContent content={data.title} />
				</Typography>
				<div className={css.description}>
					<StrapiRichContent content={data.description} />
				</div>

				<form className={css.form} onSubmit={handleSubmit(onSubmit)}>
					<fieldset disabled={isSuccess} className={css.formFieldset}>
						<div className={css.fieldsWrap}>
							<Honeypot ref={honeypotRef} />

							<div className={css.field}>
								<Controller
									name="firstName"
									control={control}
									defaultValue=""
									render={({ field }) => (
										<Input
											allowedPattern={/^[A-Za-zА-Яа-яЁёІіЇїЄєҐґ\s'-]*$/}
											{...field}
											placeholder={data.firstInputPlaceholder}
										/>
									)}
								/>

								{errors.firstName?.message ? (
									<p className={css.error}>{errors.firstName.message}</p>
								) : null}
							</div>

							<div className={css.field}>
								<Controller
									name="lastName"
									control={control}
									defaultValue=""
									render={({ field }) => (
										<Input
											allowedPattern={/^[A-Za-zА-Яа-яЁёІіЇїЄєҐґ\s'-]*$/}
											{...field}
											placeholder={data.secondInputPlaceholder}
										/>
									)}
								/>
								{errors.lastName?.message ? (
									<p className={css.error}>{errors.lastName.message}</p>
								) : null}
							</div>

							<div className={clsx(css.field, css.fullWidthField)}>
								<Controller
									name="email"
									control={control}
									defaultValue=""
									render={({ field }) => (
										<Input {...field} placeholder={data.thirdInputPlaceholder} />
									)}
								/>
								{errors.email?.message ? <p className={css.error}>{errors.email.message}</p> : null}
							</div>
						</div>

						<div className={css.footerBottom}>
							<Typography variant="body-s" tag="p" className={css.footerDescription}>
								<span>Curious how we handle your data with care? Scoop into our</span>
								&nbsp;
								<a href={routes.privacyPolicy} target="_blank" rel="noreferrer">
									Privacy Policy
								</a>
							</Typography>

							<AnimatedButton
								className={css.submitButton}
								wrapperClassName={css.submitButtonWrapper}
								buttonType="submit"
								disabled={!isValid || isSubmitting}
							>
								{data.submitButtonLabel || ''}
							</AnimatedButton>
						</div>
					</fieldset>
				</form>
			</div>

			<div
				className={clsx(
					css.videoWrapper,
					data.variationVideo === 'heads-black' && css.grayscaleVideo
				)}
			>
				<video
					key={data.variationVideo ?? 'brandbook'}
					loop
					muted
					playsInline
					className={css.video}
					controls={false}
					preload="none"
				>
					{shouldLoadVideo
						? VIDEO_SOURCE_META.map(({ key, type }) => {
								const src = videoSources[key];

								return src ? <source key={key} src={src} type={type} /> : null;
							})
						: null}
				</video>
			</div>
		</section>
	);
};

export default GuidelineForm;
